import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'

import { TApi } from '../../shared/const'
import i18nInstance from '../../shared/i18n'

import PublicTopBar from '../PublicTopBar'
import PublicFooter from '../PublicFooter'

import styles from './style.module.scss'

/**
 * Rahmen für eine der offenen Fassungen des Jahresberichts.
 *
 * Der Bericht ist ein fertiges Dokument mit eigener Gestaltung und eigener
 * Druckansicht. Er wird deshalb nicht nachgebaut, sondern in einem Rahmen
 * angezeigt — so bleibt sein Aussehen von der Anwendung unberührt und die
 * Anwendung von seinem.
 *
 * **Anders als beim Bericht der Bundesadministration steht hier eine echte
 * Adresse im `src`**, keine Objektadresse aus einem abgerufenen Blob. Das geht,
 * weil die Route offen ist: Ein Rahmen lädt ohne die Kopfzeile mit dem Zugang
 * und bekäme bei einer geschützten Route 401 — genau daran scheiterte der erste
 * Versuch beim Admin-Bericht. Hier ist es der einfachere Weg: Der Browser lädt
 * das Dokument selbst, schrittweise, und muss nicht erst drei Megabyte im
 * Speicher zusammenbauen.
 */
export default function PublicReport(props) {
	const rahmenRef = useRef(null)
	const [kompakt, setKompakt] = useState(false)

	/*
	 * Die App stellt die Sprache erst im angemeldeten Bereich auf Deutsch. Diese
	 * Seite läuft ausserhalb davon — ohne das griffe die Browsererkennung und
	 * Kopfleiste und Fusszeile erschienen auf Englisch, während der Bericht
	 * selbst deutsch ist. Dasselbe Muster wie auf der Unterstützer:innen-Seite.
	 */
	useEffect(() => {
		if (i18nInstance.language !== 'ger') i18nInstance.changeLanguage('ger')
	}, [])

	/*
	 * Der Kopf schrumpft, sobald im Bericht nach unten gescrollt wird.
	 *
	 * Warum überhaupt: Die Seite selbst scrollt nicht — der Bericht hat seinen
	 * eigenen Bildlauf im Rahmen. Der Kopf steht dadurch dauerhaft da und nahm
	 * mit Zeile, großem Titel, Anlauf und Knopf ein Viertel des Bildschirms
	 * weg, auch wenn man längst mitten im Dokument liest. Zusammengeschoben
	 * bleiben Titel und Knopf — mehr braucht man dort nicht.
	 *
	 * **Der Bericht kommt von derselben Herkunft** (`/api/public/reports/…` auf
	 * demselben Server), deshalb ist sein Bildlauf von hier aus lesbar. Käme er
	 * je von woanders, wirft der Zugriff — dann bleibt der Kopf eben groß, und
	 * die Seite funktioniert weiter. Genau dafür der `try`.
	 */
	useEffect(() => {
		const rahmen = rahmenRef.current

		if (!rahmen) return undefined

		let fenster = null

		const messen = () => {
			const oben = fenster.scrollY || fenster.document?.documentElement?.scrollTop || 0

			// Zwei Schwellen statt einer: Sonst klappt der Kopf bei einem Wert
			// genau an der Grenze bei jedem Rad-Tick auf und zu.
			setKompakt(vorher => (vorher ? oben > 60 : oben > 140))
		}

		const anbinden = () => {
			try {
				fenster = rahmen.contentWindow

				if (!fenster || !fenster.document) return

				fenster.addEventListener('scroll', messen, { passive: true })
				messen()
			} catch (fehler) {
				// Fremde Herkunft — der Kopf bleibt groß. Kein Grund für einen Fehler.
				fenster = null
			}
		}

		rahmen.addEventListener('load', anbinden)
		anbinden()

		return () => {
			rahmen.removeEventListener('load', anbinden)

			try {
				if (fenster) fenster.removeEventListener('scroll', messen)
			} catch (fehler) {
				// Der Rahmen ist schon weg — nichts abzumelden.
			}
		}
	}, [])

	return (
		<div className={styles.root}>
			<PublicTopBar />

			{/*
			 * Derselbe Aufbau wie der Aufmacher der DaVinciLab-Seite: kleine
			 * Zeile in Türkis, darunter der Titel groß und über mehrere Zeilen,
			 * darunter der Anlauf. Die drei offenen Seiten sollen als eine
			 * Familie zu erkennen sein.
			 */}
			<div className={clsx(styles.head, kompakt && styles.headKompakt)}>
				<span className={styles.kicker}>{props.kicker}</span>
				{/* Der Titel trägt seinen Umbruch im Text (`\n`) — siehe pages/PublicReport. */}
				<h1 className={styles.title}>{props.title}</h1>
				<p className={styles.sub}>{props.subtitle}</p>

				{/*
				 * Ein gewöhnlicher Verweis, kein Knopf mit Abruf: Die Route ist
				 * offen, der Browser lädt die Datei selbst und zeigt dabei seinen
				 * eigenen Fortschritt. Das PDF entsteht am Server beim ersten
				 * Abruf und wird danach behalten — der erste Klick dauert ein paar
				 * Sekunden, jeder weitere nicht.
				 */}
				<a className={styles.pdf} href={TApi.PUBLIC_REPORT_PDF(props.bericht)}>
					PDF herunterladen
				</a>
			</div>

			{/*
			 * Der Umbruch aus dem Titel gehört nicht in den Namen des Rahmens —
			 * eine Vorlesehilfe liest ihn sonst als Pause mitten im Namen.
			 */}
			<iframe
				ref={rahmenRef}
				className={styles.rahmen}
				src={TApi.PUBLIC_REPORT(props.bericht)}
				title={props.title.replace(/\n/g, ' ')}
			/>

			<PublicFooter />
		</div>
	)
}

PublicReport.propTypes = {
	/** Schlüssel der Fassung — `bilanz` oder `plattform`. */
	bericht: PropTypes.oneOf(['bilanz', 'plattform']).isRequired,
	kicker: PropTypes.string.isRequired,
	title: PropTypes.string.isRequired,
	subtitle: PropTypes.string.isRequired
}
